import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';

export interface InbuiltCardV2Detail {
  label: string;
  value: number;
  percentage: number;
  formattedValue: string;
  metricType: string;
}

type MetricType = 'SUM' | 'COUNT';

interface MetricDefinition {
  key: string;
  type: MetricType;
}

interface AggregatedMetric {
  key: string;
  type: MetricType;
  value: number;
}

@Component({
  selector: 'app-inbuilt-card-v2',
  standalone: false,
  templateUrl: './inbuilt-card-v2.component.html',
  styleUrl: './inbuilt-card-v2.component.css'
})
export class InbuiltCardV2Component {
  @Input() config: any;
  @Input() elementData: any = null;

  private readonly destroy$ = new Subject<void>();
  private selectedFilters: any = {};
  private inputSearchFilter: string = '';
  private lastTablePayloadSignature = '';
  private readonly preferredTotalKey = 'Current Charges';

  @Output() headingResolved = new EventEmitter<string>();

  displayName: string = '';
  totalLabel: string = this.preferredTotalKey;
  totalValue: number = 0;
  totalDisplayValue: string = '$0';
  details: InbuiltCardV2Detail[] = [];
  loading: boolean = false;
  noDataFound: boolean = false;

  constructor(
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService
  ) {}

  ngOnInit(): void {
    // Start with config-level filters (if available), subscribe to future changes,
    // then load the card once on initial render.
    this.selectedFilters = this.config?.selectedFilters || {};
    this.subscribeToFilterEvents();
    this.callCardDataApi();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToFilterEvents(): void {
    // This stream emits whenever filter controls update. We only react to events
    // belonging to this report/page/tab and only when API payload really changed.
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        this.selectedFilters = event?.selectedFilters || {};
        this.inputSearchFilter = event?.inputSearchFilter || '';
        this.callCardDataApiIfPayloadChanged();
      });
  }

  private callCardDataApiIfPayloadChanged(): void {
    const selectedFiltersPayload = this.buildSelectedFiltersPayload();
    const nextSignature = this.createStableSignature(selectedFiltersPayload);

    if (nextSignature === this.lastTablePayloadSignature) {
      return;
    }

    this.callCardDataApi(selectedFiltersPayload, nextSignature);
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport && eventReport !== myReport) {
      return false;
    }
    if (eventPage && eventPage !== myPage) {
      return false;
    }
    if (eventTab && eventTab !== myTab) {
      return false;
    }

    return true;
  }

  private callCardDataApi(prebuiltSelectedFiltersPayload?: any, prebuiltSignature?: string): void {
    this.loading = true;

    const selectedFiltersPayload = prebuiltSelectedFiltersPayload ?? this.buildSelectedFiltersPayload();
    const signature = prebuiltSignature ?? this.createStableSignature(selectedFiltersPayload);
    this.lastTablePayloadSignature = signature;

    const payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || 'card',
      download: false,
      selectedFilters: selectedFiltersPayload,
      inputSearchFilter: this.inputSearchFilter
    };

    this.reportsService.getInbuildTableData(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          const rows = Array.isArray(res?.data) ? res.data : [];
          this.noDataFound = rows.length === 0;
          this.bindCardData(rows, res?.columnInfo || {});
          this.bindDisplayText(res?.dynamicHeaderTitle || '');
        },
        error: (error: any) => {
          console.error('Card API error: ', error);
          this.noDataFound = true;
          this.resetCardState();
        }
      });
  }

  private buildSelectedFiltersPayload(): any {
    return this.inbuiltUtilityService.getTablePayload(this.selectedFilters);
  }

  private createStableSignature(value: any): string {
    return JSON.stringify(value || {});
  }

  private bindCardData(rows: any[], columnInfo: any): void {
    // Requirement: use columnInfo keys as source of truth for card metrics.
    // Each key is typed as SUM or COUNT (case-insensitive), then aggregated
    // across all rows.
    if (!Array.isArray(rows) || rows.length === 0) {
      this.resetCardState();
      return;
    }

    const metricDefinitions = this.extractMetricDefinitions(columnInfo);
    if (metricDefinitions.length === 0) {
      this.resetCardState();
      return;
    }

    const aggregatedMetrics = this.aggregateMetrics(rows, metricDefinitions);
    if (aggregatedMetrics.length === 0) {
      this.resetCardState();
      return;
    }

    // Requirement: if "Current Charges" exists, show it as the total metric.
    const totalMetric = this.pickTotalMetric(aggregatedMetrics);
    this.totalLabel = totalMetric.key;
    this.totalValue = totalMetric.value;
    this.totalDisplayValue = this.formatMetricValue(totalMetric.value, totalMetric.type);

    this.details = aggregatedMetrics
      .filter((metric: AggregatedMetric) => metric.key !== totalMetric.key)
      .map((metric: AggregatedMetric) => {
        const percentage = this.calculateVariancePercent(totalMetric.value, metric.value);

        return {
          label: metric.key,
          value: metric.value,
          percentage,
          formattedValue: this.formatMetricValue(metric.value, metric.type),
          metricType: metric.type
        };
      });
  }

  formatPercentValue(value: number): string {
    if (!Number.isFinite(value)) {
      return '0.00%';
    }

    return `${Math.abs(value).toFixed(2)}%`;
  }

  getPercentDirection(value: number): 'up' | 'down' | 'neutral' {
    if (!Number.isFinite(value) || value === 0) {
      return 'neutral';
    }

    return value > 0 ? 'up' : 'down';
  }

  private calculateVariancePercent(totalValue: number, currentValue: number): number {
    if (!Number.isFinite(currentValue) || currentValue === 0) {
      return 0;
    }

    return ((currentValue - totalValue) / currentValue) * 100;
  }

  private extractMetricDefinitions(columnInfo: any): MetricDefinition[] {
    if (!columnInfo || typeof columnInfo !== 'object' || Array.isArray(columnInfo)) {
      return [];
    }

    return Object.keys(columnInfo)
      .map((key: string) => {
        const metricType = this.normalizeMetricType(columnInfo[key]);
        if (!metricType) {
          return null;
        }

        return {
          key,
          type: metricType
        };
      })
      .filter((item: MetricDefinition | null): item is MetricDefinition => !!item);
  }

  private normalizeMetricType(rawType: any): MetricType | null {
    const value = String(rawType ?? '').trim().toUpperCase();
    if (value === 'SUM') {
      return 'SUM';
    }

    if (value === 'COUNT') {
      return 'COUNT';
    }

    return null;
  }

  private aggregateMetrics(rows: any[], metricDefinitions: MetricDefinition[]): AggregatedMetric[] {
    return metricDefinitions.map((definition: MetricDefinition) => {
      const value = rows.reduce((sum: number, row: any) => {
        return sum + this.toNumber(row?.[definition.key]);
      }, 0);

      return {
        key: definition.key,
        type: definition.type,
        value
      };
    });
  }

  private pickTotalMetric(metrics: AggregatedMetric[]): AggregatedMetric {
    const preferred = metrics.find((metric: AggregatedMetric) => {
      return this.normalizeKey(metric.key) === this.normalizeKey(this.preferredTotalKey);
    });

    return preferred || metrics[0];
  }

  private formatMetricValue(value: number, metricType: MetricType): string {
    if (metricType === 'SUM') {
      // SUM values are money metrics. Display in compact notation with currency.
      return this.formatCompact(value, '$');
    }

    // COUNT values should be displayed as pure numbers without currency.
    return this.formatNumber(value);
  }

  private formatCompact(value: number, currencySymbol: string): string {
    if (!Number.isFinite(value)) {
      return `${currencySymbol}0`;
    }

    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);

    if (absValue >= 1_000_000_000) {
      return `${sign}${currencySymbol}${(absValue / 1_000_000_000).toFixed(2)}B`;
    }

    if (absValue >= 1_000_000) {
      return `${sign}${currencySymbol}${(absValue / 1_000_000).toFixed(2)}M`;
    }

    if (absValue >= 1_000) {
      return `${sign}${currencySymbol}${(absValue / 1_000).toFixed(2)}K`;
    }

    const formatted = absValue.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return `${sign}${currencySymbol}${formatted}`;
  }

  private formatNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }

    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  private normalizeKey(value: any): string {
    return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const parsed = parseFloat(String(value).replace(/[$,%\s]/g, '').replace(/,/g, '').trim());
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private resetCardState(): void {
    this.totalLabel = this.preferredTotalKey;
    this.totalValue = 0;
    this.totalDisplayValue = '$0';
    this.details = [];
  }

  bindDisplayText(displayText: any): void {
    if (displayText && Object.keys(displayText).length > 0) {
      let key = this.elementData?.name || '';
      // remove start{ and end brackets}
      if (key.startsWith('{') && key.endsWith('}')) {
        key = key.slice(1, -1).trim();
      }

      const resolved = displayText[key] || '';
      if (resolved) {
        this.displayName = resolved;
        this.headingResolved.emit(resolved);
      }
    }
  }
}
